import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsApi } from "@/features/tasks/api/notifications";
import { broadcastRefreshRequest } from "@/features/tasks/hooks/useNotificationSync";
import type { NotificationCategory } from "@/features/tasks/types";

const notifKeys = {
  all: ["task-notifications"] as const,
  list: () => [...notifKeys.all, "list"] as const,
  // Pełne archiwum siedzi pod osobnym kluczem, żeby polling lidera (który pobiera
  // domyślne okno) nie nadpisywał listy wczytanej przyciskiem „Wczytaj starsze".
  fullHistory: () => [...notifKeys.all, "list", "full"] as const,
  unreadCount: () => [...notifKeys.all, "unread-count"] as const,
};

/** Odświeżanie listy, dopóki dzwonek jest otwarty (w tej karcie, widocznej). */
const OPEN_LIST_POLL_MS = 30_000;

/**
 * Lista powiadomień dzwonka — pobierana tylko, gdy `enabled` (dzwonek otwarty).
 * Lider (useNotificationSync) pilnuje samych liczników i unieważnia listę, gdy się
 * zmienią; własny interwał łapie nowe zdarzenia w wątku, który już był nieprzeczytany
 * (licznik liczy wątki, więc wtedy się nie rusza).
 */
export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: notifKeys.list(),
    queryFn: () => notificationsApi.list(),
    enabled,
    refetchInterval: enabled ? OPEN_LIST_POLL_MS : false,
  });
}

/** Całe archiwum — pobierane dopiero po kliknięciu „Wczytaj starsze". */
export function useNotificationsFullHistory(enabled: boolean) {
  return useQuery({
    queryKey: notifKeys.fullHistory(),
    queryFn: () => notificationsApi.list({ fullHistory: true }),
    enabled,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: notifKeys.unreadCount(),
    queryFn: () => notificationsApi.unreadCount(),
    // j.w. — sync hook zastępuje polling
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notifKeys.all });
      // Poproś lidera o natychmiastowy fetch + broadcast, żeby inne karty
      // też zobaczyły zmianę bez czekania do następnego cyklu (max 30s).
      broadcastRefreshRequest();
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    // Kategoria = otwarta zakładka; undefined czyści wszystko (zakładka „Wszystkie").
    mutationFn: (category?: NotificationCategory) => notificationsApi.markAllRead(category),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notifKeys.all });
      broadcastRefreshRequest();
    },
  });
}

export function useTogglePin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.togglePin(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notifKeys.all });
      broadcastRefreshRequest();
    },
  });
}
